import { type PayloadAction, createAsyncThunk, createSelector, createSlice } from "@reduxjs/toolkit";

import type { OrganizationUser } from "@/Dashboard/pages/Organizations/OrganizationUsers/types";
import { REQUEST_STATUSES } from "@/constants/requestStatuses";
import { OrganizationsService } from "@/services/OrganizationsService";
import type { State } from "@/store/types";

export interface OrganizationUsersState {
  [organizationId: number]: typeof defaultState;
}

interface SetOrganizationUsersPayload {
  organizationId: number;
  data: OrganizationUser[];
  count: number;
  status: REQUEST_STATUSES;
  err: unknown;
  noResults: boolean;
}

const defaultState = {
  data: [] as OrganizationUser[],
  status: REQUEST_STATUSES.IDLE as REQUEST_STATUSES,
  count: 0 as number,
  err: null as unknown,
  noResults: false as boolean,
} as const;

const initialState: OrganizationUsersState = {};

export const fetchOrganizationUsers = createAsyncThunk(
  "organizationUsers/fetchOrganizationUsers",
  async ({ organizationId, query }: { organizationId: number; query?: unknown }) => {
    const { resp, status, err } = await OrganizationsService.getUsers(organizationId, query);
    const { data: users = [], count = 0 } = resp || {};

    return {
      organizationId,
      users,
      count,
      status,
      err,
      query,
    };
  },
);

const organizationUsersSlice = createSlice({
  name: "organizationUsers",
  initialState,
  reducers: {
    resetOrganizationUsers: () => initialState,
    setOrganizationUsersReqStatus: (
      state,
      action: PayloadAction<{ organizationId: number; status: REQUEST_STATUSES }>,
    ) => {
      const { organizationId, status } = action.payload;
      if (!state[organizationId]) {
        state[organizationId] = { ...defaultState };
      }
      state[organizationId].status = status;
    },
    setOrganizationUsers: (state, action: PayloadAction<SetOrganizationUsersPayload>) => {
      const { organizationId } = action.payload;

      if (!state[organizationId]) {
        state[organizationId] = { ...defaultState };
      }

      state[organizationId].data = action.payload.data;
      state[organizationId].count = action.payload.count;
      state[organizationId].status = action.payload.status;
      state[organizationId].err = action.payload.err;
      state[organizationId].noResults = action.payload.noResults;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchOrganizationUsers.pending, (state, action) => {
        const organizationId = action.meta.arg.organizationId;
        if (!state[organizationId]) {
          state[organizationId] = { ...defaultState };
        }
        state[organizationId].status = REQUEST_STATUSES.LOADING;
        state[organizationId].err = null;
      })
      .addCase(fetchOrganizationUsers.fulfilled, (state, action) => {
        const { organizationId, users, status, err, count } = action.payload;
        state[organizationId] = {
          ...state[organizationId],
          data: users,
          count,
          status,
          err,
          noResults: users.length === 0,
        };
      })
      .addCase(fetchOrganizationUsers.rejected, (state, action) => {
        const organizationId = action.meta.arg.organizationId;
        if (state[organizationId]) {
          state[organizationId].status = REQUEST_STATUSES.FAILED;
          state[organizationId].err = action.error;
        }
      });
  },
});

export const organizationUsersSelector = (organizationId: number) =>
  createSelector(
    (state: State) => state.organizationUsers,
    (organizationUsers) => ({
      ...defaultState,
      ...organizationUsers?.[organizationId],
    }),
  );

export const { setOrganizationUsersReqStatus } = organizationUsersSlice.actions;
export const { setOrganizationUsers } = organizationUsersSlice.actions;
export default organizationUsersSlice.reducer;
